import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ShieldCheck, Lock, Eye, UserX, Trash2, Image as ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Sticker } from "@/components/Stickers";
import { SEO, breadcrumbLd, webPageLd } from "@/lib/seo";

const TITLE = "Safety";
const DESC =
  "How DoppelCrush keeps you safe: 18+ only, selfie privacy, block & report tools, full data deletion, and what we never do with your face.";

const items = [
  { icon: UserX,       title: "18+ only",                 body: "DoppelCrush is for adults. You confirm you're 18 or older during onboarding and any account we suspect to be under 18 is removed immediately." },
  { icon: ImageIcon,   title: "Only upload your own photo", body: "Don't upload someone else's selfie, a celebrity, a cartoon or anything misleading. Catfishing is an instant ban — we use server-side image moderation to catch the obvious ones." },
  { icon: ShieldCheck, title: "Selfie privacy",           body: "Your selfie is stored privately. The 128-number face embedding is computed inside your browser (face-api.js, open-source) so we never receive raw biometric data. We never share your selfie outside the app or with third-party biometric services." },
  { icon: Lock,        title: "Encrypted accounts",       body: "Passwords are hashed with bcrypt. Sessions use signed JWTs. We don't sell your data and never run third-party ad trackers." },
  { icon: Eye,         title: "Block & report",           body: "One tap to block or report any profile or message. Reports go to safety@doppelcrush.com and we review fast." },
  { icon: Trash2,      title: "Delete your data",         body: "Profile → Delete account wipes your selfie, embedding, swipes, matches and messages immediately. You can also email hello@doppelcrush.com for a manual data export or deletion." },
];

export default function Safety() {
  return (
    <div className="crush-bg min-h-screen" data-testid="safety-page">
      <SEO
        title={TITLE}
        description={DESC}
        path="/safety"
        jsonLd={[
          webPageLd({ title: TITLE, description: DESC, path: "/safety" }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Safety", path: "/safety" },
          ]),
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        <Header />
        <div className="crush-frame mt-4 p-6 sm:p-10 relative">
          <Sticker kind="heart" size={64} className="absolute -top-6 -right-4 rotate-12" />
          <h1 className="font-display text-5xl font-bold text-slate-900">Safety &amp; privacy</h1>
          <p className="mt-3 text-slate-600 font-body max-w-2xl text-lg">
            Playful, never creepy. Here&apos;s what we do — and what we never do — with your face card and your data.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {items.map((s) => (
              <div key={s.title} className="bg-white rounded-3xl p-6 border-2 border-pink-100">
                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 grid place-items-center"><s.icon className="w-5 h-5" /></div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-3">{s.title}</h2>
                <p className="font-body text-slate-600 mt-1">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <h2 className="font-display text-3xl font-bold">Reporting checklist</h2>
            <ul className="mt-3 list-disc pl-5 space-y-1.5 font-body text-slate-700">
              <li>Suspected minor on the app → <a href="mailto:safety@doppelcrush.com" className="text-pink-600 underline">safety@doppelcrush.com</a></li>
              <li>Harassment, hate speech, threats → use in-chat report, plus email safety@</li>
              <li>Fake selfie / catfish → tap Report on the profile</li>
              <li>Scam / financial solicitation → tap Report and block</li>
            </ul>
          </div>

          <div className="mt-10 text-sm font-body text-slate-600">
            See also <Link to="/privacy" className="text-pink-600 underline">Privacy Policy</Link>, <Link to="/terms" className="text-pink-600 underline">Terms of Service</Link>, <Link to="/faq" className="text-pink-600 underline">FAQ</Link>.
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
